<script setup>
import Layout from "../Layouts/Customer.vue";
import {useForm, Link} from "@inertiajs/inertia-vue3";
import CustomerDevicesDialog from "../Components/CustomerDevicesDialog.vue";
import {useToast} from "primevue/usetoast";
import {computed, onMounted, ref, watch, nextTick} from "vue";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import _ from 'lodash'
import Calendar from "primevue/calendar";
import Message from 'primevue/message';
import Tag from 'primevue/tag'
import OverlayPanel from 'primevue/overlaypanel';
import DeviceReasonsOP from "../Components/DeviceReasonsOP.vue";
import CustomerPlanUtils from "../utils/CustomerPlanUtils"
import Dialog from 'primevue/dialog';
import SimplybookIcon from "../Components/SimplybookIcon.vue";

 const props = defineProps([
    'customer',
    'menuItems',
    'flash',
    'programOptions',
    'max2HoursOptions',
    'devices',
    'customerProgram',
    'deviceReasonsMap',
    'customerDeviceLimits',
    'deviceCoverageMap',
    'customerPlanBookingsMap',
    'latestProgresses'
]);

const customerPlan = ref(props.customer.plan)
const hasCustomerPlan = ref(Boolean(customerPlan.value))
const deviceLimits = ref(props.customerDeviceLimits || {})
const editingDeviceId = ref(null)
const tempLimit = ref(null)
const form = useForm({
    program: props.customerProgram ?? null,
    max_two_hours_per_week: props.customer.answer?.max_two_hours_per_week ?? '2',
    months: null,
    devices: props.customer.devices ?? [],
    planMatrix: [],
    scheduleMatrix: [],
    device_limits: deviceLimits.value
})
const devicesDialog = ref(null)
const submitting = ref(false)
const devicesCountMap = ref({})
const toast = useToast();
const deviceReasonsOP = ref(null)
const resetForm = useForm({
    limit: null
})
const showResetDialog = ref(false)
const deviceToReset = ref(null)

const devicesEnum = ref({
    BODYSHAPE_V2_EXCLUSIVE      : 'Bodyshape-V2-Multivision',
    AVANTGARDE_MEDICAL          : 'Avantgarde Medical',
    DERMIO_CARE_PLUS            : 'Dermio Care Plus',
    INSHAPE                     : 'Inshape',
    ICOONE_LASER_MED            : 'Icoone Laser Med',
    ALMA_PRIMEX                 : 'Alma Primex',
    COOLTECH_DEFINE             : 'Cooltech Define',
    PNEUMATRON                  : 'Pneumatron',
    CELLGYM                     : 'Cellgym',
    MEDICAL_CAT                 : 'Medical CAT',
    BTL_EMSELLA                 : 'BTL Emsella',
    EMSCULPT                    : 'Emsculpt'
})
const initialForm = ref(null)
const programPlanDataMap = ref({
    'overweight': {
        units: 109,
        max_two_hours_per_week: '3',
        otherDevices: [
            devicesEnum.value.BODYSHAPE_V2_EXCLUSIVE
        ],
        matrixData: {
            is_loaded: false,
            months: 3,
            matrix: [
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.ALMA_PRIMEX
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.EMSCULPT
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.EMSCULPT
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.EMSCULPT
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.EMSCULPT
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.EMSCULPT
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.ALMA_PRIMEX
                    ]
                ],
                [
                    [
                        devicesEnum.value.EMSCULPT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.ALMA_PRIMEX
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.ALMA_PRIMEX
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.ALMA_PRIMEX
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.ALMA_PRIMEX
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ]
            ]
        }
    },

    'stress': {
        max_two_hours_per_week: '3',
        units: 72,
        matrixData: {
            is_loaded: false,
            months: 2,
            matrix: [
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.DERMIO_CARE_PLUS
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.PNEUMATRON
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.PNEUMATRON
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.DERMIO_CARE_PLUS
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.PNEUMATRON
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ]
            ]
        }
    },

    'cellulite': {
        max_two_hours_per_week: '2',
        units: 48,
        matrixData: {
            is_loaded: false,
            months: 2,
            matrix: [
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.ALMA_PRIMEX
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.ALMA_PRIMEX
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.ALMA_PRIMEX
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.ALMA_PRIMEX
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ]
                ],
                [
                    [
                        devicesEnum.value.ICOONE_LASER_MED
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ]
                ]
            ]
        }
    },

    'tiredness': {
        max_two_hours_per_week: '3',
        units: 68,
        otherDevices: [
            devicesEnum.value.BODYSHAPE_V2_EXCLUSIVE
        ],
        matrixData: {
            is_loaded: false,
            months: 2,
            matrix: [
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        null
                    ]
                ],
                [
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        null
                    ]
                ],
                [
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        null
                    ]
                ],
                [
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.MEDICAL_CAT,
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        null
                    ]
                ]
            ]
        }
    },

    'longevity': {
        max_two_hours_per_week: '3',
        units: 73,
        otherDevices: [
            devicesEnum.value.BODYSHAPE_V2_EXCLUSIVE
        ],
        matrixData: {
            is_loaded: false,
            months: 2,
            matrix: [
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.PNEUMATRON
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.DERMIO_CARE_PLUS
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.ICOONE_LASER_MED
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.DERMIO_CARE_PLUS
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.ICOONE_LASER_MED,
                        devicesEnum.value.MEDICAL_CAT
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.CELLGYM
                    ]
                ],
                [
                    [
                        devicesEnum.value.CELLGYM
                    ],
                    [
                        devicesEnum.value.AVANTGARDE_MEDICAL,
                        devicesEnum.value.PNEUMATRON
                    ],
                    [
                        devicesEnum.value.CELLGYM
                    ]
                ]
            ]
        },
        devicesMap: {}
    },

    'incontinence': {
        max_two_hours_per_week: '2',
        units: 8,
        matrixData: {
            is_loaded: false,
            months: 1,
            matrix: [
                [
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ],
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ]
                ],
                [
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ],
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ]
                ],
                [
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ],
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ]
                ],
                [
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ],
                    [
                        devicesEnum.value.BTL_EMSELLA
                    ]
                ]
            ]
        },
        devicesMap: {}
    }
})



const max2Hours = computed(() => {
    return parseInt(form.max_two_hours_per_week)
})

const defaultDeviceOptions = computed(() => {
    const options = []

    form.devices.forEach((data) => {
        options.push({
            value: data.id,
            label: data.name
        })
    })

    return options
})

const deviceNameIdMap = computed(() => {
    const map = {}

    for (var key in devicesEnum.value) {
        const name = devicesEnum.value[key]
        const devicesIdx = props.devices.findIndex((deviceData) => {
            return deviceData.name == name
        })

        if (devicesIdx !== -1) {
            map[name] = props.devices[devicesIdx].id
        }
    }

    return map
})

const deviceIdMap = computed(() => {
    const map = {}

    props.devices.forEach((v) => {
        return map[v.id] = v
    })

    return map
})

const programUnits = computed(() => {
    // Calculate sum of all device limits
    let totalLimits = 0;
    
    // Sum limits from form.devices
    form.devices.forEach(device => {
        const limit = getLimitCount(device);
        if (typeof limit === 'number') {
            totalLimits += limit;
        }
    });
    
    return totalLimits;
})

const customerProgramOptions = computed(() => {
    const options = []

    props.customer.answer.programs.forEach((v) => {
        props.programOptions.every((vProgramOption) => {
            if (vProgramOption.value == v) {
                options.push(vProgramOption)

                return false
            }

            return true
        })
    })

    return options
})

const reduceWeightMessage = computed(() => {
    if (! props.customer.answer.want_to_reduce_weight) {
        return null
    }


    const rawKgPerMonth = parseFloat(props.customer.answer.reduce_weight) / form.months
    const roundedKgPerPerMonth = parseFloat(rawKgPerMonth.toFixed(2))
    const kgPerMonth = (roundedKgPerPerMonth % 1 == 0)
        ? parseInt(roundedKgPerPerMonth)
        : roundedKgPerPerMonth
    const isProgramSuccessful = (rawKgPerMonth <= 3)

    return isProgramSuccessful
        ? `Diese Ergebnis kann mit Behandlungseinheiten ${form.max_two_hours_per_week}x2h/Woche in ${form.months} Monaten erreicht werden. Das sind ${kgPerMonth.toString()} kg pro Monat`
        : `Das Ergebnis ist Behandlungseinheiten ${form.max_two_hours_per_week}x2h/Woche in ${form.months} Monaten nicht zu erreichen. Das sind ${kgPerMonth.toString()} kg pro Monat`
})

const defaultMonths = computed(() => {
    return programPlanDataMap.value[form.program]
        ? programPlanDataMap.value[form.program].matrixData.months
        : 3
})

const dayColAuth = computed(() => {
    let value = ''

    for (let i = 0; i < max2Hours.value; i++) {
        value = value + '_auto'
    }

    return value
})

const currentDefaultMonths = computed(() => {
    let value = defaultMonths.value

    const programData = programPlanDataMap.value[form.program]

    if (programData) {
        const currentProgramMatrix = CustomerPlanUtils.organize(parseInt(form.max_two_hours_per_week), programData.matrixData.matrix)

        value = Math.ceil(currentProgramMatrix.length / 4)
    }

    return value
})

const removeDevice = (idx) => {
    form.devices.splice(idx, 1)
    devicesDialog.value.setAvailableDevices()
}

const fillPlanMatrix = (currentMax2Hours) => {
    for (let i = 0; i < form.months; i++) {
        for (let j = 0; j < 4; j++) {
            const data = []

            for (let k = 0; k < currentMax2Hours; k++) {
                data.push([null])
            }

            form.planMatrix.push(data)
        }
    }

    setScheduleMatrix()
}

const submit = async (e, isDownload) => {
    submitting.value = true
    
    // Sync device limits before submission
    updateDeviceLimits();

    form.post(`/customers/${props.customer.uuid}/plan`, {
        preserveState: false,
        onSuccess: (page) => {
            if (page.props.flash) {
                toast.add({
                    severity: page.props.flash.severity,
                    summary: page.props.flash.summary,
                    detail: page.props.flash.detail,
                    life: 8000
                });
            } else {
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000
                });
            }

            if (isDownload) {
                window.location.href = `/customers/${props.customer.uuid}/plan/export`
            }
        },

        onFinish: (visit) => {
            submitting.value = false;
        }
    })
}

const getCurrentProgramPlanMatrix = () => {
    const programData = programPlanDataMap.value[form.program]
    const currentProgramMatrix = CustomerPlanUtils.organize(parseInt(form.max_two_hours_per_week), programData.matrixData.matrix)

    if (programData.matrixData.is_loaded) {
        return currentProgramMatrix
    }

    const planMatrix = []

    for (let i = 0; i < currentProgramMatrix.length; i++) {
        const rowData = []

        for (let j = 0; j < currentProgramMatrix[i].length; j++) {
            const colData = []

            for (let k = 0; k < currentProgramMatrix[i][j].length; k++) {
                const value = currentProgramMatrix[i][j][k]
                
                if (value && deviceNameIdMap.value[value]) {
                    colData.push(deviceNameIdMap.value[value])
                }
            }

            rowData.push(colData)
        }

        planMatrix.push(rowData)
    }

    programPlanDataMap.value[form.program].matrixData.matrix = planMatrix
    programPlanDataMap.value[form.program].matrixData.is_loaded = true

    return planMatrix
}

const onProgramChange = () => {
    if (initialForm.value && form.program == initialForm.value.program) {
        form.program = initialForm.value.program
        form.max_two_hours_per_week = initialForm.value.max_two_hours_per_week
        form.months = initialForm.value.months
        form.devices = initialForm.value.devices
        form.planMatrix = initialForm.value.planMatrix
        form.scheduleMatrix = initialForm.value.scheduleMatrix
        form.device_limits = initialForm.value.device_limits

        return
    }

    updatematrixData()
}
const updatematrixData = () => {
    if (typeof programPlanDataMap.value[form.program] == 'undefined') {
        form.months = 3
        form.planMatrix = []
        form.devices = []

        fillPlanMatrix(max2Hours.value)
        devicesDialog.value.setAvailableDevices()

        return
    }

    const programData = programPlanDataMap.value[form.program]

    setFormCurrentPlanMatrix()

    form.months =  Math.ceil(form.planMatrix.length / 4)
}

const onMaxTwoHoursPerWeekChange = () => {
    if (typeof programPlanDataMap.value[form.program] == 'undefined') {
        form.months = 3
        form.planMatrix = []
        form.devices = []

        fillPlanMatrix(max2Hours.value)
        devicesDialog.value.setAvailableDevices()

        return
    }

    const max2HoursPerWeek = parseInt(form.max_two_hours_per_week)
    const planMatrix = CustomerPlanUtils.organize(max2HoursPerWeek, form.planMatrix)

    setCurrentProgramDevices(planMatrix)

    form.planMatrix = planMatrix
    form.scheduleMatrix = CustomerPlanUtils.organizeDates(max2HoursPerWeek, form.scheduleMatrix)

    form.months =  Math.ceil(form.planMatrix.length / 4)
}

const setCurrentProgramDevices = (planMatrix) => {
    const ids = [...new Set(planMatrix.flat(Infinity))].filter(Number)
    const devices = []

    ids.forEach((v) => {
        devices.push(deviceIdMap.value[v])
    })

    if (typeof programPlanDataMap.value[form.program] != 'undefined' &&
        typeof programPlanDataMap.value[form.program].otherDevices != 'undefined') {
        programPlanDataMap.value[form.program].otherDevices.forEach((v, i) => {
            if (typeof v != 'object') {
                const deviceId = deviceNameIdMap.value[v]
                if (deviceId && deviceIdMap.value[deviceId]) {
                    programPlanDataMap.value[form.program].otherDevices[i] = deviceIdMap.value[deviceId]
                    devices.push(programPlanDataMap.value[form.program].otherDevices[i])
                }
            } else {
                devices.push(v)
            }
        })
    }

    form.devices = devices

    devicesDialog.value.setAvailableDevices()
}

const setFormCurrentPlanMatrix = () => {
    const planMatrix = getCurrentProgramPlanMatrix()

    setCurrentProgramDevices(planMatrix)

    form.planMatrix = planMatrix

    setScheduleMatrix()
}

const setScheduleMatrix = () => {
    const scheduleMatrix = []

    for (let i = 0; i < form.planMatrix.length; i++) {
        const rowData = []

        for (let j = 0; j < form.planMatrix[i].length; j++) {
            rowData.push(null)
        }

        scheduleMatrix.push(rowData)
    }

    form.scheduleMatrix = scheduleMatrix
}

const handleDateChange = (newDate, planMatrixDataIdx, colPlanMatrixDataIdx) => {
    if (newDate) {
        form.scheduleMatrix[planMatrixDataIdx][colPlanMatrixDataIdx] =  new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);
    }
}

const addMonth = () => {
    const planMatrixData = []

    for (let i = 0; i < max2Hours.value; i++) {
        planMatrixData.push([null])
    }

    form.planMatrix.push(planMatrixData)

    const scheduleMatrixData = []

    for (let j = 0; j < form.planMatrix[form.planMatrix.length - 1].length; j++) {
        scheduleMatrixData.push(null)
    }

    form.scheduleMatrix.push(scheduleMatrixData)

    form.months = Math.ceil(form.planMatrix.length / 4)
}

const removePlanItem = (idx) => {
    form.planMatrix.splice(idx, 1)
    form.scheduleMatrix.splice(idx, 1)

    form.months = Math.ceil(form.planMatrix.length / 4)
}

const updateDeviceLimits = () => {
    // Collect ALL device limits (both edited and calculated)
    const allDeviceLimits = {};
    
    // Iterate through all devices in the form
    form.devices.forEach(device => {
        // Get the limit value - either custom (edited) or calculated
        const limitValue = deviceLimits.value[device.id] !== undefined 
            ? deviceLimits.value[device.id]  // Use custom limit if exists
            : getLimitCount(device);          // Otherwise use calculated limit
            
        // Include all device limits in the payload
        allDeviceLimits[device.id] = limitValue;
    });
    
    // Update form.device_limits with ALL device limits
    form.device_limits = allDeviceLimits;
}

const togglePopOver = (e, device) => {
    deviceReasonsOP.value.togglePopOver(e, device)
}

watch(
    defaultDeviceOptions,
    (newVal, oldVal) => {
        // delete values in the plan matrix that is not in the user's devices
        const map = {}

        newVal.forEach(function(data) {
            map[data.value] = true
        })

        for (let i = 0; i < form.planMatrix.length; i++) {
            for (let j = 0; j < form.planMatrix[i].length; j++) {
                const idxToDelete = []

                for (let k = 0; k < form.planMatrix[i][j].length; k++) {
                    const value = form.planMatrix[i][j][k]

                    if (value !== null && ! map[value]) {
                        idxToDelete.push(k)
                    }
                }

                idxToDelete.forEach((value) => {
                    form.planMatrix[i][j].splice(value, 1)
                })
            }
        }
    }
)

watch(
    () => _.cloneDeep(form.planMatrix),
    (newVal, oldVal) => {
        const map = {}

        form.planMatrix.forEach((data) => {
            data.forEach((value) => {
                value.forEach((id) => {
                    if (id) {
                        if (map[id]) {
                            map[id]++
                        } else {
                            map[id] = 1
                        }
                    }
                })
            })
        })

        let total = 0

        Object.keys(map).forEach((value) => {
            total += map[value]
        })

        map.total = total

        devicesCountMap.value = map
    }
)

const download = async () => {
    if (! submitting.value) {
        await submit(null, true)
    }
}

const getLimitCount = (data) => {
    // Check for custom limit first
    if (deviceLimits.value[data.id] !== undefined) {
        return deviceLimits.value[data.id];
    }
    
    // If limit is null, calculate from plan_items/matrix
    if (data.limit === null) {
        // Count occurrences in planMatrix
        let count = 0;
        form.planMatrix.forEach(month => {
            month.forEach(day => {
                if (Array.isArray(day)) {
                    count += day.filter(deviceId => deviceId === data.id).length;
                }
            });
        });
        
        // Return the count (0 if nothing found)
        return count;
    }

    const devicesCountMapValue = (devicesCountMap.value[data.id] ?? 0)

    if (! hasCustomerPlan.value) {
        return data.limit + devicesCountMapValue
    }

    const diff = devicesCountMapValue - data.limit

    return Math.abs(diff) + devicesCountMapValue
}

const hasCustomLimit = (deviceId) => {
    return deviceLimits.value[deviceId] !== undefined;
}

const resetProgress = (device) => {
    deviceToReset.value = device;
    showResetDialog.value = true;
}

const confirmReset = () => {
    if (!deviceToReset.value) return;
    
    resetForm.limit = getLimitCount(deviceToReset.value);
    resetForm.post(`/customers/${props.customer.uuid}/reset-progress/${deviceToReset.value.uuid}`, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolg',
                detail: 'Fortschritt wurde erfolgreich zurückgesetzt',
                life: 3000
            });
            showResetDialog.value = false;
            deviceToReset.value = null;
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Fehler',
                detail: 'Fortschritt konnte nicht zurückgesetzt werden',
                life: 3000
            });
            showResetDialog.value = false;
            deviceToReset.value = null;
        }
    });
}

const cancelReset = () => {
    showResetDialog.value = false;
    deviceToReset.value = null;
}

const getDeviceProgress = (deviceId) => {
    // Get current active bookings count
    const currentCount = props.customerPlanBookingsMap && props.customerPlanBookingsMap[deviceId] 
        ? props.customerPlanBookingsMap[deviceId].length 
        : 0;
    
    // Get historical progress if exists
    const historicalProgress = props.latestProgresses && props.latestProgresses[deviceId] 
        ? props.latestProgresses[deviceId] 
        : null;
    
    // Get device limit
    const limit = deviceLimits.value[deviceId] || 0;
    
    return {
        current: currentCount,
        limit: limit,
        historical: historicalProgress
    };
}

const getDeviceCoverage = (deviceId) => {
    return (props.deviceCoverageMap || {})[deviceId] || null;
}

const startEditLimit = (device) => {
    if (getDeviceCoverage(device.id)) {
        toast.add({
            severity: 'info',
            summary: 'Kein manuelles Limit möglich',
            detail: 'Dieses Gerät ist bereits durch ein aktives Paket oder Abo gedeckt.',
            life: 5000,
        });
        return;
    }

    editingDeviceId.value = device.id;
    tempLimit.value = deviceLimits.value[device.id] || getLimitCount(device);
    // Focus the input after Vue updates the DOM
    nextTick(() => {
        const input = document.querySelector(`input[data-device-id="${device.id}"]`);
        if (input) input.focus();
    });
}

const updateLimit = async (device) => {
    // Update local state and form data
    if (tempLimit.value !== null && tempLimit.value !== '' && !isNaN(tempLimit.value) && tempLimit.value >= 0) {
        deviceLimits.value[device.id] = parseInt(tempLimit.value);
        form.device_limits[device.id] = parseInt(tempLimit.value);
        
        // Auto-save the limit change
        await submit(null, false);
    }
    editingDeviceId.value = null;
    tempLimit.value = null;
}

const cancelEditLimit = () => {
    editingDeviceId.value = null;
    tempLimit.value = null;
}

onMounted(() => {
    if (props.customer?.is_measurement_stale) {
        toast.add({
            severity: 'error',
            summary: 'Messung veraltet',
            detail: 'Die Messungen dieses Kunden wurden seit über 3 Wochen nicht aktualisiert.',
            life: 0,
            closable: true
        });
    }

    if (! hasCustomerPlan.value) {
        updatematrixData()

        return
    }

    form.months = customerPlan.value?.months
        ? customerPlan.value.months
        : defaultMonths.value

    // Determine the actual max columns needed based on existing plan items
    let requiredColumns = max2Hours.value;
    props.customer.plan_items.forEach((item) => {
        if (item.day_index >= requiredColumns) {
            requiredColumns = item.day_index + 1;
        }
    });
    
    fillPlanMatrix(requiredColumns)

    let lastMonthIdx = null

    props.customer.plan_items.forEach((data) => {
        // Safety check: ensure the matrix indices exist
        if (!form.planMatrix[data.month_index] || !form.planMatrix[data.month_index][data.day_index]) {
            console.warn(`PlanMatrix index out of bounds: month ${data.month_index}, day ${data.day_index}`);
            return;
        }
        
        const filtered = form.planMatrix[data.month_index][data.day_index].filter(Boolean)

        filtered.push(data.device_id)

        form.planMatrix[data.month_index][data.day_index] = filtered

        lastMonthIdx = data.month_index
    })

    props.customer.schedule_entries.forEach((data) => {
        // Safety check: ensure the matrix indices exist
        if (!form.scheduleMatrix[data.month_index] || !form.scheduleMatrix[data.month_index][data.day_index] === undefined) {
            console.warn(`ScheduleMatrix index out of bounds: month ${data.month_index}, day ${data.day_index}`);
            return;
        }
        form.scheduleMatrix[data.month_index][data.day_index] = new Date(data.date)
    })

    // Fix: Initialize devices if customer has plan but no devices attached
    if (form.devices.length === 0 && form.program && hasCustomerPlan.value && form.planMatrix.length > 0) {
        // Call setCurrentProgramDevices with the already populated planMatrix
        setCurrentProgramDevices(form.planMatrix)
    }

    let currentMatrixDataMonths = defaultMonths.value
    const programData = programPlanDataMap.value[form.program]

    if (programData) {
        const currentProgramMatrix = CustomerPlanUtils.organize(parseInt(form.max_two_hours_per_week), programData.matrixData.matrix)

        currentMatrixDataMonths = Math.ceil(currentProgramMatrix.length / 4)
    }

    if (form.months > currentMatrixDataMonths) {
        const defaultMonthLastIdx = (4 * currentMatrixDataMonths) - 1
        const latestMonthLastIdx = (4 * form.months) - 1


        if (lastMonthIdx < latestMonthLastIdx && lastMonthIdx !== null && lastMonthIdx > defaultMonthLastIdx) {
            for (var i = latestMonthLastIdx; i > lastMonthIdx; i--) {
                form.planMatrix.pop()
                form.scheduleMatrix.pop()
            }
        } else if (lastMonthIdx < latestMonthLastIdx && (lastMonthIdx === null || lastMonthIdx <= defaultMonthLastIdx)) {
            const nextDefaultMonthLastIdx = defaultMonthLastIdx + 1

            for (var i = latestMonthLastIdx; i > nextDefaultMonthLastIdx; i--) {
                form.planMatrix.pop()
                form.scheduleMatrix.pop()
            }
        }
    }

    initialForm.value = {
        program: form.program,
        max_two_hours_per_week: form.max_two_hours_per_week,
        months: form.months,
        devices: [...form.devices], // Clone to preserve initial state
        planMatrix: form.planMatrix,
        scheduleMatrix: form.scheduleMatrix,
        device_limits: form.device_limits
    }
})

</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="py-7">
            <div class="px-14 mb-7">
                <div class="relative mb-9">
                    <h1 class="text-black text-2xl font-regular">
                        Trainings- und Behandlungsplan
                    </h1>

                    <Tag :severity="hasCustomerPlan ? 'success' : 'warning'">
                        {{ hasCustomerPlan ? 'Aktiv' : 'Inaktiv' }}
                    </Tag>

                    <div class="absolute top-0 right-0">
                        <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer" @click="download"></i>
                    </div>
                </div>

                <Message v-if="customer.answer.want_to_reduce_weight"
                         severity="info"
                         :closable="false">
                    <span class="text-sm">
                        Bei diesem Fall sind {{ customer.answer.reduce_weight }} kg zu reduzieren.
                        {{ reduceWeightMessage }}
                    </span>
                </Message>

                <div class="grid grid-cols-[1fr_1fr_1fr_auto] gap-x-[3em] gap-y-4">
                    <div>
                        <h4 class="text-black text-sm font-bold">Abo-Programm-Wahl</h4>
                    </div>

                    <div>
                        <h4 class="text-black text-sm font-bold">Behandlungseinheiten</h4>
                    </div>

                    <div>
                        <h4 class="text-black text-sm font-bold">Behandlungsdauer</h4>
                    </div>

                    <div>
                        <!-- Empty header for save button -->
                    </div>

                    <div>
                        <Dropdown
                            v-model="form.program"
                            :options="customerProgramOptions"
                            option-label="label"
                            option-value="value"
                            class="w-full md:w-14rem"
                            @change="onProgramChange" />
                    </div>

                    <div>
                        <Dropdown
                            v-model="form.max_two_hours_per_week"
                            :options="max2HoursOptions"
                            option-label="label"
                            option-value="value"
                            class="w-full md:w-14rem"
                            @change="onMaxTwoHoursPerWeekChange" />
                    </div>

                    <div class="h-full">
                        <div class="flex flex-col h-full justify-center">
                            <span>{{ form.months }} Monate</span>
                        </div>
                    </div>

                    <div class="h-full flex items-center justify-end">
                        <Button
                            @click="submit"
                            :loading="submitting"
                            label="speichern"
                            class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                        />
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-6 gap-0 items-center mb-9">
                <div class="bg-[#EEEBE4] col-span-2 h-full">
                    <div class="pl-14 pr-2 py-3">
                        <h4 class="text-black text-xs font-extralight">Analyse</h4>
                    </div>
                </div>

                <div class="bg-[#EEEBE4] h-full">
                    <div class="px-2 py-3 flex justify-end">
                        <h4 class="text-black text-xs font-extralight">Total Einheiten:</h4>
                    </div>
                </div>

                <div class="bg-[#EEEBE4] h-full">
                    <div class="px-2 py-[10px]">
                        <h4 class="text-black text-sm flex items-center">
                            <span class="font-bold">{{ programUnits }}</span>
                        </h4>
                    </div>
                </div>

                <div class="bg-[#EEEBE4] col-span-2 h-full">
                    <div class="pl-2 pr-14 py-3">
                        <div class="flex justify-end gap-2">
                            <h4 class="text-black text-xs font-extralight">weitere Behandlung hinzufügen</h4>
                            <i class="pi pi-plus text-[#C5C6CC] cursor-pointer"
                               @click="devicesDialog.open()"></i>
                        </div>
                    </div>
                </div>

                <template v-for="(data, idx) in form.devices" :key="`devices_${idx}`">
                    <div class="border-b border-[#C5C6CC] col-span-2 h-full">
                        <div class="pl-14 pr-2 py-3">
                            <span class="text-sm">{{ data.name }}</span>
                        </div>
                    </div>

                    <div class="border-b border-[#C5C6CC] h-full">
                        <div class="px-2 py-3 flex">
                        </div>
                    </div>

                    <div class="border-b border-[#C5C6CC] col-span-2 h-full">
                        <div class="px-2 py-3">
                            <div class="flex items-center justify-between">
                                <h4 class="text-black text-sm font-bold">
                                    <!-- Current Progress -->
                                    <Link :href="`/bookings/${customer.uuid}/${data.uuid}`"
                                         class="text-black hover:text-blue-600 underline">
                                        {{ getDeviceProgress(data.id).current }}
                                    </Link>
                                    <span> / </span>
                                    
                                    <!-- Display Mode -->
                                    <template v-if="editingDeviceId !== data.id">
                                        <span v-if="getDeviceCoverage(data.id)"
                                              class="text-gray-500"
                                              :title="`Gedeckt durch: ${getDeviceCoverage(data.id).label}`">
                                            ∞
                                            <span class="text-xs ml-1 px-1 py-0.5 rounded bg-gray-100 text-gray-600 font-normal">
                                                {{ getDeviceCoverage(data.id).type === 'package' ? 'Paket' : 'Abo' }}
                                            </span>
                                        </span>
                                        <span v-else
                                              @click="startEditLimit(data)"
                                              class="cursor-pointer hover:text-blue-600 transition-colors"
                                              :class="hasCustomLimit(data.id) ? 'font-bold text-blue-600' : ''">
                                            {{ getLimitCount(data) }}
                                            <i class="pi pi-pencil text-xs ml-1"></i>
                                        </span>

                                        <!-- Show historical progress if exists -->
                                        <span v-if="getDeviceProgress(data.id).historical" 
                                              class="text-gray-500 text-xs ml-2">
                                            (Zurückgesetzt von {{ getDeviceProgress(data.id).historical.count }}/{{ getDeviceProgress(data.id).historical.limit }})
                                        </span>
                                    </template>
                                    
                                    <!-- Edit Mode - Simple text input -->
                                    <template v-else>
                                        <input 
                                            type="number"
                                            v-model="tempLimit"
                                            @blur="updateLimit(data)"
                                            @keyup.enter="updateLimit(data)"
                                            @keyup.escape="cancelEditLimit"
                                            :data-device-id="data.id"
                                            min="0"
                                            max="999"
                                            class="w-16 px-1 py-0 text-sm border border-gray-300 rounded"
                                            :class="hasCustomLimit(data.id) ? 'font-bold text-blue-600' : ''"
                                        />
                                    </template>
                                </h4>
                                
                                <!-- Action Buttons -->
                                <div class="flex items-center">
                                    <!-- History Link -->
                                    <Link 
                                        :href="`/customers/${customer.uuid}/progress-history/${data.uuid}`"
                                        class="text-purple-600 hover:text-purple-700 p-1"
                                        title="View Progress History"
                                    >
                                        <i class="pi pi-chart-line text-sm"></i>
                                    </Link>
                                    
                                    <!-- Reset Button -->
                                    <button 
                                        @click="resetProgress(data)"
                                        class="text-red-500 hover:text-red-700 ml-2 p-1"
                                        title="Reset Progress"
                                    >
                                        <i class="pi pi-refresh text-sm"></i>
                                    </button>
                                    
                                    <!-- SimplyBook Bookings Link -->
                                    <Link 
                                        :href="`/customers/${customer.uuid}/simplybook-bookings/${data.uuid}`"
                                        class="text-blue-600 hover:text-blue-700 ml-2 p-1"
                                        title="SimplyBook Buchungen anzeigen"
                                    >
                                        <SimplybookIcon :size="14" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-b border-[#C5C6CC] h-full">
                        <div class="pl-2 pr-14 py-[16px]">
                            <div class="flex gap-3 justify-end">
                                <div v-if="data.pivot?.warning_type"
                                      class="flex gap-3"
                                     @mouseover="togglePopOver($event, data)">
                                    <i class="pi pi-exclamation-circle"
                                        :class="{
                                            'text-[#FF0000]': data.pivot?.warning_type == 'no',
                                            'text-[#FFC000]': data.pivot?.warning_type == 'maybe'
                                        }"></i>

                                    <span class="text-xs font-extralight">Freigabe durch VIGORY nötig</span>
                                </div>

                                <i class="pi pi-trash text-[#C5C6CC] cursor-pointer" @click="removeDevice(idx)"></i>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <div :class="`grid grid-cols-[56px_70px${dayColAuth}_56px] gap-0 items-center mb-9`">
                <div class="bg-[#EEEBE4] h-full w-[56px] col-span-1">
                    &nbsp;
                </div>

                <div class="bg-[#EEEBE4] h-full col-span-1">
                    <div class="pr-2 py-3">
                        <h4 class="text-black text-xs font-extralight">Woche</h4>
                    </div>
                </div>


                <div v-for="i in max2Hours"
                     class="bg-[#EEEBE4] h-full"
                     :key="`max_hours_${i}`">
                    <div class="pl-2 py-3" :class="{
                        'pr-0': i == max2Hours,
                        'pr-3': i != max2Hours
                    }">
                        <div class="text-black text-xs font-extralight">{{ i }}. Tag</div>
                    </div>
                </div>

                <div class="bg-[#EEEBE4] h-full w-[56px] col-span-1">
                    &nbsp;
                </div>

                <template v-for="(planMatrixData, planMatrixDataIdx) in form.planMatrix"
                          :key="`plan_${planMatrixDataIdx}`">
                    <div class="border-b border-[#C5C6CC] h-full">
                        &nbsp;
                    </div>

                    <div class="border-b border-[#C5C6CC] h-full">
                        <div class="pr-2 py-3">
                            <h4 class="text-black text-sm font-bold">{{ planMatrixDataIdx + 1 }}</h4>
                        </div>
                    </div>

                    <div v-for="(colPlanMatrixData, colPlanMatrixDataIdx) in planMatrixData"
                         class="border-b border-[#C5C6CC] h-full"
                         :key="`col_${colPlanMatrixDataIdx}`">
                        <div class="pl-2 py-3" :class="{
                            'pr-0': colPlanMatrixDataIdx >= max2Hours - 1,
                            'pr-3': colPlanMatrixDataIdx < max2Hours - 1
                        }">
                            <div class="grid grid-cols-1 gap-y-2">
                                <div v-for="(deviceValue, deviceValueIdx) in colPlanMatrixData"
                                     :key="`device_${deviceValueIdx}`">
                                    <Dropdown
                                        v-model="form.planMatrix[planMatrixDataIdx][colPlanMatrixDataIdx][deviceValueIdx]"
                                        :options="defaultDeviceOptions"
                                        option-label="label"
                                        option-value="value"
                                        class="w-full">
                                    </Dropdown>
                                </div>

                                <div class="flex justify-between">
                                    <i class="pi pi-minus text-[#C5C6CC] cursor-pointer"
                                       @click="form.planMatrix[planMatrixDataIdx][colPlanMatrixDataIdx].pop()"></i>

                                    <i class="pi pi-plus text-[#C5C6CC] cursor-pointer"
                                       @click="form.planMatrix[planMatrixDataIdx][colPlanMatrixDataIdx].push(null)"></i>
                                </div>

                                <Calendar
                                    v-model="form.scheduleMatrix[planMatrixDataIdx][colPlanMatrixDataIdx]"
                                    showIcon
                                    iconDisplay="input"
                                    class="w-full"
                                    dateFormat="dd.mm.yy"
                                    placeholder="tt.mm.jjjj"
                                    @date-select="handleDateChange($event, planMatrixDataIdx, colPlanMatrixDataIdx)"
                                />
                            </div>

                            <div v-if="(planMatrixDataIdx > (4 * currentDefaultMonths) - 1) && (colPlanMatrixDataIdx >= max2Hours - 1)"
                                 class="mt-2">
                                <div class="flex justify-end">
                                    <i class="pi pi-trash text-[#C5C6CC] cursor-pointer"
                                       @click="removePlanItem(planMatrixDataIdx)"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-b border-[#C5C6CC] h-full">
                        &nbsp;
                    </div>
                </template>
            </div>

            <div class="px-14">
                <div class="flex justify-between items-center">
                    <a href="#"
                       @click.prevent="addMonth">
                        <i class="pi pi-plus"></i>
                        hinzufügen
                    </a>

                    <Button
                        @click="submit"
                        :loading="submitting"
                        label="speichern"
                        class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                    />
                </div>
            </div>
            
            <!-- Hidden form fields for device limits -->
            <template v-for="(limit, deviceId) in deviceLimits" :key="`limit_${deviceId}`">
                <input type="hidden" :name="`device_limits[${deviceId}]`" :value="limit" />
            </template>
        </div>

        <CustomerDevicesDialog :form="form"
                               :devices="devices"
                               ref="devicesDialog"></CustomerDevicesDialog>

        <DeviceReasonsOP ref="deviceReasonsOP"
                         :device-reasons-map="deviceReasonsMap"></DeviceReasonsOP>

        <div class="grid grid-cols-5 hidden"></div>
        <div class="grid grid-cols-[56px_70px_auto_56px] hidden"></div>
        <div class="grid grid-cols-[56px_70px_auto_auto_56px] hidden"></div>
        <div class="grid grid-cols-[56px_70px_auto_auto_auto_56px] hidden"></div>
        <div class="grid grid-cols-[56px_70px_auto_auto_auto_auto_56px] hidden"></div>
        
        <Dialog v-model:visible="showResetDialog" 
                modal 
                header="Fortschritt zurücksetzen" 
                :style="{ width: '450px' }">
            <div class="flex items-center gap-3 mb-5">
                <i class="pi pi-exclamation-triangle text-orange-500 text-2xl"></i>
                <span v-if="deviceToReset">
                    Möchten Sie den Fortschritt für {{ deviceToReset.name }} wirklich zurücksetzen? 
                    Der aktuelle Fortschritt ({{ getDeviceProgress(deviceToReset.id).current }}/{{ getLimitCount(deviceToReset) }}) 
                    wird im Verlauf gespeichert.
                </span>
            </div>
            
            <template #footer>
                <Button label="Abbrechen" 
                        @click="cancelReset" 
                        class="p-button-text" />
                <Button label="Ja, zurücksetzen" 
                        @click="confirmReset" 
                        class="p-button-danger" 
                        :loading="resetForm.processing" />
            </template>
        </Dialog>
    </Layout>

</template>
